import type { CvBankAddResult, CvBankDuplicatePair, CvBankEntryView } from '@nest-recruit/shared';
import { formatDate } from '../../lib/format';

// ADR 0046 — the words the CV Bank's buttons and toasts use, kept apart so they are tested.

/** Q4's buttons: "Keep Karim's, 3 Oct" — the date tells two CVs of one person apart. */
export function keepLabel(entry: CvBankEntryView): string {
  return `Keep ${firstName(entry.candidate.fullName)}'s, ${formatDate(entry.addedAt)}`;
}

/** Why two entries were put side by side. */
export function pairReason(pair: CvBankDuplicatePair): string {
  if (pair.kind === 'same_person') return 'The same person, twice';
  return pair.confidence === null ? 'Possibly the same person' : `Possibly the same person (${pair.confidence}% match)`;
}

/** The toast after an add: who went in, and who did not and why. */
export function addedMessage(result: CvBankAddResult, names: readonly string[]): { readonly message: string; readonly detail?: string } {
  const added = result.addedEntryIds.length;
  const skippedDetail = result.skipped.length > 0 ? result.skipped.map((skip) => skip.reason).join(' ') : undefined;
  if (added === 0) return { message: 'Nobody was added to the CV Bank', ...(skippedDetail ? { detail: skippedDetail } : {}) };
  const who = names.length === 1 && added === 1 && names[0] ? names[0] : `${added} ${added === 1 ? 'person' : 'people'}`;
  return { message: `${who} added to the CV Bank`, ...(skippedDetail ? { detail: skippedDetail } : {}) };
}

export function duplicatesMessage(pairs: readonly CvBankDuplicatePair[]): string {
  const [only] = pairs;
  if (pairs.length === 1 && only) return `${only.second.candidate.fullName} may already be in the CV Bank`;
  return `${pairs.length} possible duplicates in the CV Bank`;
}

function firstName(fullName: string): string {
  const words = fullName.trim().split(/\s+/);
  // "Md. Rahim Uddin" reads as Rahim, not "Md.".
  const named = words.find((word) => !/^(md|mohammad|mohammed|muhammad|mst|mrs?|ms|dr)\.?$/i.test(word));
  return named ?? words[0] ?? fullName;
}

/** The CV drawer's Previous / Next (Redesign Group 8): it steps through the rows on the page. */
export interface EntrySteps {
  readonly position: number;
  readonly total: number;
  readonly previousId: string | null;
  readonly nextId: string | null;
}

/** Null when the open entry is not on this page (a link to it): it opens alone, with no steps. */
export function entrySteps(rows: readonly CvBankEntryView[], openId: string): EntrySteps | null {
  const index = rows.findIndex((row) => row.id === openId);
  if (index < 0) return null;
  return {
    position: index + 1,
    total: rows.length,
    previousId: rows[index - 1]?.id ?? null,
    nextId: rows[index + 1]?.id ?? null,
  };
}

/** Spaces, dashes and brackets go before sending, as Add a candidate does: "01712-345 678" is a valid mobile. */
export function cleanPhone(value: string): string | null {
  const cleaned = value.replace(/[\s\-()]/g, '');
  return cleaned === '' ? null : cleaned;
}
